import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { MagnifyingGlassIcon, EyeIcon, BanknotesIcon, DocumentTextIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import { UserAPI } from '@/services/api'
import { useResponsive } from '@/contexts/ResponsiveContext'
import ResponsiveTable, { createColumn, TablePagination } from '@/components/ui/ResponsiveTable'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import BalanceManagementModal from '@/components/modals/BalanceManagementModal'
import MemoManagementModal from '@/components/modals/MemoManagementModal'
import RoleManagementModal from '@/components/modals/RoleManagementModal'
import useAuthStore from '@/stores/authStore'

export default function UsersPage() {
  const navigate = useNavigate()
  const { isMobile, getCardSpacing } = useResponsive()
  const { user: currentUser } = useAuthStore()
  
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [sortConfig, setSortConfig] = useState(null)
  const [balanceModalOpen, setBalanceModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [memoModalOpen, setMemoModalOpen] = useState(false)
  const [selectedUserForMemo, setSelectedUserForMemo] = useState(null)
  const [roleModalOpen, setRoleModalOpen] = useState(false)
  const [selectedUserForRole, setSelectedUserForRole] = useState(null)

  useEffect(() => {
    fetchUsers()
  }, [currentPage, searchTerm, roleFilter])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const requestedPage = currentPage
      const response = await UserAPI.getUsers({
        page: requestedPage,
        limit: 10,
        search: searchTerm,
        role: roleFilter !== 'all' ? roleFilter : undefined
      })

      if (response.success) {
        const { users: fetchedUsers = [], pagination = {} } = response.data || {}

        const resolvedTotalPages = Math.max(1, pagination.totalPages || 1)
        setTotalPages(resolvedTotalPages)

        if (requestedPage > resolvedTotalPages) {
          if (currentPage !== resolvedTotalPages) {
            setCurrentPage(resolvedTotalPages)
          }
          return
        }

        setUsers(fetchedUsers)

        const resolvedPage = pagination.page || requestedPage
        if (resolvedPage !== currentPage) {
          setCurrentPage(resolvedPage)
        }
      }
    } catch (error) {
      console.error('Failed to fetch users:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    setCurrentPage(1)
  }

  const handleSort = (sortData) => {
    setSortConfig(sortData)
    // In a real app, you'd send this to the API
    // For now, we'll just store the sort config
  }

  const handleRowClick = (user) => {
    navigate(`/dashboard/users/${user.id}`)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleBalanceManagement = (user, e) => {
    e.stopPropagation()
    setSelectedUser(user)
    setBalanceModalOpen(true)
  }

  const handleBalanceModalClose = () => {
    setBalanceModalOpen(false)
    setSelectedUser(null)
  }

  const handleBalanceUpdated = () => {
    fetchUsers() // Refresh the users list
  }

  const handleMemoManagement = (user, e) => {
    e.stopPropagation()
    setSelectedUserForMemo(user)
    setMemoModalOpen(true)
  }

  const handleMemoModalClose = () => {
    setMemoModalOpen(false)
    setSelectedUserForMemo(null)
  }

  const handleRoleManagement = (user, e) => {
    e.stopPropagation()
    setSelectedUserForRole(user)
    setRoleModalOpen(true)
  }

  const handleRoleModalClose = () => {
    setRoleModalOpen(false)
    setSelectedUserForRole(null)
  }

  const handleRoleUpdated = () => {
    fetchUsers() // Refresh the users list
  }

  // Define table columns
  const columns = [
    createColumn({
      key: 'user',
      header: '사용자',
      priority: 'high',
      essential: true,
      render: (_, user) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {user.name || user.nickname}
          </div>
          <div className="text-sm text-gray-500">
            {user.nickname && user.name !== user.nickname && `@${user.nickname} • `}@{user.username}
          </div>
        </div>
      )
    }),
    createColumn({
      key: 'role',
      header: '역할',
      priority: 'high',
      essential: true,
      render: (role) => {
        const roleConfig = {
          'user': { bg: 'bg-blue-100', text: 'text-blue-800', label: '사용자' },
          'admin': { bg: 'bg-green-100', text: 'text-green-800', label: '관리자' },
          'super_admin': { bg: 'bg-purple-100', text: 'text-purple-800', label: '최고 관리자' }
        }
        const config = roleConfig[role] || roleConfig['user']
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}>
            {config.label}
          </span>
        )
      }
    }),
    createColumn({
      key: 'krwBalance',
      header: '원화 잔액',
      priority: 'medium',
      sortable: true,
      render: (balance) => `₩${Number(balance || 0).toLocaleString()}`
    }),
    createColumn({
      key: 'tokenBalance',
      header: '토큰 잔액',
      priority: 'medium',
      sortable: true,
      render: (balance) => Number(balance || 0).toLocaleString()
    }),
    createColumn({
      key: 'createdAt',
      header: '가입일',
      priority: 'low',
      sortable: true,
      render: (date) => new Date(date).toLocaleDateString('ko-KR')
    }),
    createColumn({
      key: 'actions',
      header: '액션',
      priority: 'medium',
      render: (_, user) => (
        <div className="flex items-center gap-2">
          <Link
            to={`/dashboard/users/${user.id}`}
            className="text-blue-600 hover:text-blue-900 flex items-center gap-1 transition-colors duration-200"
            onClick={(e) => e.stopPropagation()}
            title="상세보기"
          >
            <EyeIcon className="h-4 w-4" />
            {!isMobile && '상세보기'}
          </Link>
          {['admin', 'super_admin'].includes(currentUser?.role) && (
            <button
              onClick={(e) => handleBalanceManagement(user, e)}
              className="text-green-600 hover:text-green-900 flex items-center gap-1 transition-colors duration-200"
              title="잔액관리"
            >
              <BanknotesIcon className="h-4 w-4" />
              {!isMobile && '잔액관리'}
            </button>
          )}
          <button
            onClick={(e) => handleMemoManagement(user, e)}
            className="text-purple-600 hover:text-purple-900 flex items-center gap-1 transition-colors duration-200"
            title="메모관리"
          >
            <DocumentTextIcon className="h-4 w-4" />
            {!isMobile && '메모관리'}
          </button>
          {currentUser?.role === 'super_admin' && (
            <button
              onClick={(e) => handleRoleManagement(user, e)}
              className="text-orange-600 hover:text-orange-900 flex items-center gap-1 transition-colors duration-200"
              title="권한관리"
            >
              <ShieldCheckIcon className="h-4 w-4" />
              {!isMobile && '권한관리'}
            </button>
          )}
        </div>
      )
    })
  ]

  return (
    <div className={getCardSpacing()}>
      {/* Header */}
      <div className={`flex ${isMobile ? 'flex-col space-y-4' : 'flex-row justify-between items-center'}`}>
        <h1 className={`font-bold text-gray-900 ${isMobile ? 'text-xl' : 'text-2xl'}`}>
          사용자 관리
        </h1>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        {/* Role Filter */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">역할별 필터</h3>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all', label: '전체' },
              { key: 'user', label: '사용자' },
              { key: 'admin', label: '관리자' },
              { key: 'super_admin', label: '최고관리자' }
            ].map((item) => (
              <button
                key={item.key}
                onClick={() => setRoleFilter(item.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  roleFilter === item.key
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className={`flex ${isMobile ? 'flex-col space-y-3' : 'flex-row gap-4'}`}>
          <div className="flex-1">
            <Input
              type="text"
              placeholder="사용자명, 닉네임, 이름으로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              leftIcon={MagnifyingGlassIcon}
              size={isMobile ? 'md' : 'sm'}
            />
          </div>
          <Button
            type="submit"
            variant="secondary"
            size={isMobile ? 'md' : 'sm'}
            className={isMobile ? 'w-full' : ''}
          >
            검색
          </Button>
        </form>
      </div>

      {/* Users Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <ResponsiveTable
          data={users}
          columns={columns}
          loading={loading}
          emptyMessage="사용자가 없습니다."
          onRowClick={handleRowClick}
          sortable={true}
          onSort={handleSort}
          sortConfig={sortConfig}
          className="min-h-[400px]"
        />
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          showPageNumbers={!isMobile}
        />
      )}

      {/* Balance Management Modal */}
      <BalanceManagementModal
        isOpen={balanceModalOpen}
        onClose={handleBalanceModalClose}
        user={selectedUser}
        onBalanceUpdated={handleBalanceUpdated}
      />

      {/* Memo Management Modal */}
      <MemoManagementModal
        isOpen={memoModalOpen}
        onClose={handleMemoModalClose}
        user={selectedUserForMemo}
      />

      {/* Role Management Modal */}
      <RoleManagementModal
        isOpen={roleModalOpen}
        onClose={handleRoleModalClose}
        user={selectedUserForRole}
        onRoleUpdated={handleRoleUpdated}
      />
    </div>
  )
}
